import React, { useEffect, useState } from 'react'
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Chip
} from '@mui/material'
import {
  People,
  Agriculture,
  Store,
  PendingActions,
  ShoppingCart,
  Receipt
} from '@mui/icons-material'
import { API_BASE_URL } from '../config/api'

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<{
    stat1: number; stat2: number; stat3: number; stat4: number;
    labels: string[]; icons: string[]; colors: string[]; isCurrency: boolean[];
  } | null>(null)
  const [recentActivity, setRecentActivity] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) return

        const res = await fetch(`${API_BASE_URL}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        if (res.ok) {
          const json = await res.json()
          if (json.success && json.data) {
            setStats(json.data)
            setRecentActivity(json.data.recent_activity || [])
          }
        }
      } catch (err) {
        console.error('Failed to load dashboard stats:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const formatTimeAgo = (dateStr: string) => {
    if (!dateStr) return ''
    const now = new Date()
    const d = new Date(dateStr)
    const diffMs = now.getTime() - d.getTime()
    const mins = Math.floor(diffMs / 60000)
    const hours = Math.floor(diffMs / 3600000)
    const days = Math.floor(diffMs / 86400000)
    if (mins < 1) return 'Just now'
    if (mins < 60) return `${mins}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'order': return <ShoppingCart fontSize="small" />
      case 'production': return <Agriculture fontSize="small" />
      case 'user': return <People fontSize="small" />
      case 'requirement': return <Receipt fontSize="small" />
      default: return <Receipt fontSize="small" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': case 'completed': case 'delivered': case 'active': return 'success'
      case 'pending': case 'processing': case 'confirmed': case 'planned': return 'warning'
      case 'cancelled': case 'rejected': case 'failed': return 'error'
      default: return 'default'
    }
  }

  const statCards = stats
    ? [
        { title: stats.labels[0], value: stats.stat1, isCurrency: stats.isCurrency[0], icon: <People fontSize="large" /> },
        { title: stats.labels[1], value: stats.stat2, isCurrency: stats.isCurrency[1], icon: <PendingActions fontSize="large" /> },
        { title: stats.labels[2], value: stats.stat3, isCurrency: stats.isCurrency[2], icon: <Agriculture fontSize="large" /> },
        { title: stats.labels[3], value: stats.stat4, isCurrency: stats.isCurrency[3], icon: <Store fontSize="large" /> },
      ]
    : [
        { title: 'Total Users', value: 0, isCurrency: false, icon: <People fontSize="large" /> },
        { title: 'Pending Approvals', value: 0, isCurrency: false, icon: <PendingActions fontSize="large" /> },
        { title: 'Active Productions', value: 0, isCurrency: false, icon: <Agriculture fontSize="large" /> },
        { title: 'Marketplace Listings', value: 0, isCurrency: false, icon: <Store fontSize="large" /> },
      ]

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        FamaLink Admin Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" component="div">
                      {stat.isCurrency
                        ? `$${(stat.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : (stat.value || 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box color="primary.main">
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            {recentActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {loading ? 'Loading…' : 'No recent activity'}
              </Typography>
            ) : (
              <Box>
                {recentActivity.slice(0, 8).map((a, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: i < recentActivity.slice(0, 8).length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box color="text.secondary">{getActivityIcon(a.type)}</Box>
                      <Box>
                        <Typography variant="body2">{a.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeAgo(a.time)}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={a.detail}
                      size="small"
                      color={getStatusColor(a.detail) as any}
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                API Server
              </Typography>
              <LinearProgress variant="determinate" value={100} color="success" />

              <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                Database
              </Typography>
              <LinearProgress variant="determinate" value={95} color="success" />

              <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                Redis Cache
              </Typography>
              <LinearProgress variant="determinate" value={88} color="warning" />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Dashboard
